import React, { useState, useEffect } from 'react';
import './App.css';

const FALLBACK_VAPID_PUBLIC_KEY = '';

/**
 * Основной компонент для тестирования push-уведомлений
 * @returns {JSX.Element} React компонент
 */
function App() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [login, setLogin] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [status, setStatus] = useState('');
  const [vapidPublicKey, setVapidPublicKey] = useState(FALLBACK_VAPID_PUBLIC_KEY);

  useEffect(() => {
    checkPushSupport();
    checkNotificationPermission();
    fetchVapidPublicKey();
  }, []);

  /**
   * Проверяет поддержку push-уведомлений в браузере
   */
  const checkPushSupport = () => {
    setIsSupported('serviceWorker' in navigator && 'PushManager' in window);
  };

  /**
   * Проверяет текущее разрешение на уведомления
   */
  const checkNotificationPermission = () => {
    setPermission(Notification.permission);
  };

  /**
   * Запрашивает разрешение на уведомления
   * @returns {Promise<void>}
   */
  const requestNotificationPermission = async () => {
    const result = await Notification.requestPermission();
    setPermission(result);
    setStatus(`Permission: ${result}`);
  };

  /**
   * Загружает публичный VAPID ключ с бэкенда
   * @returns {Promise<void>}
   */
  const fetchVapidPublicKey = async () => {
    try {
      const response = await fetch('/api/notifications/vapid-public-key');

      if (!response.ok) {
        setStatus('Failed to load VAPID public key');
        return;
      }

      const data = await response.json();
      setVapidPublicKey(data.public_key || '');
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      setStatus('Error loading VAPID public key');
    }
  };

  /**
   * Конвертирует base64 строку в Uint8Array
   * @param {string} base64String - строка в формате base64
   * @returns {Uint8Array} массив байтов
   */
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  /**
   * Подписывается на push-уведомления
   * @returns {Promise<void>}
   */
  const subscribeToPush = async () => {
    if (!login.trim()) {
      setStatus('Please enter login');
      return;
    }

    if (!vapidPublicKey) {
      setStatus('VAPID public key is not loaded');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      
      if (existingSubscription) {
        setSubscription(existingSubscription);
        await sendSubscriptionToBackend(existingSubscription);
        return;
      }

      const newSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      setSubscription(newSubscription);
      await sendSubscriptionToBackend(newSubscription);
    } catch (error) {
      console.error('Error subscribing to push:', error);
      setStatus(`Error: ${error.message || 'Unknown error'}`);
    }
  };

  /**
   * Отправляет данные подписки на бэкенд
   * @param {PushSubscription} subscription - объект подписки
   * @returns {Promise<void>}
   */
  const sendSubscriptionToBackend = async (subscription) => {
    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(subscription.getKey('p256dh'))))),
        auth: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(subscription.getKey('auth')))))
      }
    };

    try {
      const response = await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login: login,
          endpoint: subscriptionData.endpoint,
          keys: subscriptionData.keys
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(`Subscribed successfully! Client ID: ${data.client_id}`);
        fetchSubscribers();
      } else {
        setStatus('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      setStatus('Error sending subscription');
    }
  };

  /**
   * Получает список подписчиков с бэкенда
   * @returns {Promise<void>}
   */
  const fetchSubscribers = async () => {
    try {
      const response = await fetch('/api/notifications/subscribers');
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  /**
   * Отправляет тестовое уведомление
   * @returns {Promise<void>}
   */
  const sendNotification = async () => {
    if (!login.trim() || !notificationTitle.trim() || !notificationBody.trim()) {
      setStatus('Please fill all fields');
      return;
    }

    try {
      const response = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login: login,
          title: notificationTitle,
          body: notificationBody,
          data: { timestamp: new Date().toISOString() }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(`Notification sent to ${data.to}`);
      } else {
        setStatus('Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setStatus('Error sending notification');
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Push Notification Test</h1>
        
        <div className="status-section">
          <p>Push Support: {isSupported ? '✅' : '❌'}</p>
          <p>Notification Permission: {permission}</p>
          <p>Subscription: {subscription ? '✅ Active' : '❌ Not active'}</p>
        </div>

        {!isSupported && (
          <div className="warning">
            Your browser does not support push notifications.
          </div>
        )}

        {permission === 'default' && (
          <button onClick={requestNotificationPermission}>
            Request Notification Permission
          </button>
        )}

        {permission === 'granted' && (
          <div className="subscription-section">
            <div className="input-group">
              <label>Login:</label>
              <input
                type="text"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Enter your login"
              />
            </div>

            <button onClick={subscribeToPush} disabled={!login.trim()}>
              Subscribe to Push Notifications
            </button>

            <div className="notification-section">
              <h3>Send Test Notification</h3>
              <div className="input-group">
                <label>Title:</label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="Notification title"
                />
              </div>
              <div className="input-group">
                <label>Body:</label>
                <input
                  type="text"
                  value={notificationBody}
                  onChange={(e) => setNotificationBody(e.target.value)}
                  placeholder="Notification body"
                />
              </div>
              <button onClick={sendNotification} disabled={!login.trim()}>
                Send Notification
              </button>
            </div>

            <div className="subscribers-section">
              <h3>Subscribers</h3>
              <button onClick={fetchSubscribers}>Refresh List</button>
              <ul>
                {subscribers.map((sub) => (
                  <li key={sub.id}>
                    {sub.login} - {sub.id.substring(0, 8)}...
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {status && (
          <div className="status-message">
            <p>{status}</p>
          </div>
        )}
      </header>
    </div>
  );
}

export default App;
